// Function index:
//   prefsEditor(partyId, prefs, host, ctx, onClose) — DOM-only editor for one
//     party's gm_prefs row. Lays out every boolean flag, the interval inputs,
//     the nullable per-user spend cap, and a read-only state block. On Save
//     it diffs the form into a Partial<PartyPrefs>, posts it, and triggers a
//     fresh renderParties() of the host so the list reflects the new values.
//
// Why split: the editor is 150+ lines of pure DOM construction with no shared
// state with the list renderer — keeping it next to parties.ts but in its own
// file makes both readable in one screen.

import { setPartyPrefs, type PartyPrefs } from '../../../api.js';
import { esc, type AdminPanelCtx } from '../helpers.js';
import { renderParties } from '../parties.js';

export function prefsEditor(
  partyId: number,
  prefs: PartyPrefs,
  host: HTMLElement,
  ctx: AdminPanelCtx,
  onClose: () => void,
): HTMLDivElement {
  const editor = document.createElement('div');
  editor.style.display = 'grid';
  editor.style.gridTemplateColumns = '1fr 1fr';
  editor.style.gap = '6px 18px';

  // Booleans (the feature toggles). Narrative-extract is intentionally
  // not exposed — it's load-bearing for any GM behaviour now that the
  // inline tools were removed in Phase 4.
  const bools: Array<[keyof PartyPrefs, string]> = [
    ['continuity_guardian', 'Continuity guardian'],
    ['auto_lore', 'Auto-lore'],
    ['pacing_director', 'Pacing director'],
    ['consequence_engine', 'Consequence engine'],
    ['session_recap', 'Session recap'],
    ['npc_simulation', 'NPC simulation'],
    ['arc_summaries', 'Arc summaries'],
    ['director_note', 'Director note'],
  ];

  const inputs: Partial<Record<keyof PartyPrefs, HTMLInputElement>> = {};

  for (const [key, label] of bools) {
    const row = document.createElement('label');
    row.style.fontSize = '13px'; row.style.color = '#ddd';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = Boolean(prefs[key]);
    cb.style.marginRight = '8px';
    row.appendChild(cb);
    row.appendChild(document.createTextNode(label));
    editor.appendChild(row);
    inputs[key] = cb;
  }

  const intervals: Array<[keyof PartyPrefs, string]> = [
    ['pacing_interval', 'Pacing interval (turns)'],
    ['session_recap_threshold_minutes', 'Recap gap (minutes)'],
    ['director_interval', 'Director interval (turns)'],
  ];
  for (const [key, label] of intervals) {
    const row = document.createElement('label');
    row.style.fontSize = '13px'; row.style.color = '#aaa'; row.style.gridColumn = 'span 1';
    const input = document.createElement('input');
    input.type = 'number';
    input.min = '1';
    input.value = String(prefs[key]);
    input.style.cssText = 'width:80px;background:#1c1c1c;color:#eee;border:1px solid #2c2c2c;padding:4px 8px;font:inherit;font-size:13px;border-radius:3px;margin-left:8px;';
    row.textContent = `${label}: `;
    row.appendChild(input);
    editor.appendChild(row);
    inputs[key] = input;
  }

  const capRow = document.createElement('label');
  capRow.style.cssText = 'font-size:13px; color:#aaa; grid-column: span 2;';
  capRow.textContent = 'Per-user daily Anthropic cap (USD, blank = no cap): ';
  const capInput = document.createElement('input');
  capInput.type = 'number';
  capInput.step = '0.01';
  capInput.min = '0';
  capInput.style.cssText = 'width:100px;background:#1c1c1c;color:#eee;border:1px solid #2c2c2c;padding:4px 8px;font:inherit;font-size:13px;border-radius:3px;margin-left:8px;';
  capInput.value = prefs.per_user_daily_usd_cap === null ? '' : String(prefs.per_user_daily_usd_cap);
  capInput.placeholder = '(none)';
  capRow.appendChild(capInput);
  editor.appendChild(capRow);
  inputs['per_user_daily_usd_cap'] = capInput;

  const stateBlock = document.createElement('div');
  stateBlock.style.gridColumn = 'span 2';
  stateBlock.style.marginTop = '8px';
  stateBlock.style.fontSize = '12px';
  stateBlock.style.color = '#888';
  const fragments: string[] = [];
  fragments.push(`<div>pacing_turn_counter: ${prefs.pacing_turn_counter} · director_turn_counter: ${prefs.director_turn_counter}</div>`);
  if (prefs.pacing_nudge) fragments.push(`<div>current pacing nudge: <span style="color:#ddd;">${esc(prefs.pacing_nudge)}</span></div>`);
  if (prefs.director_hint) fragments.push(`<div>current director hint: <span style="color:#ffeb88;">${esc(prefs.director_hint)}</span></div>`);
  stateBlock.innerHTML = fragments.join('');
  editor.appendChild(stateBlock);

  const buttonRow = document.createElement('div');
  buttonRow.style.gridColumn = 'span 2';
  buttonRow.style.display = 'flex';
  buttonRow.style.gap = '8px';
  buttonRow.style.marginTop = '10px';
  buttonRow.style.flexWrap = 'wrap';

  const save = document.createElement('button');
  save.textContent = 'Save';
  save.addEventListener('click', async () => {
    const patch: Partial<PartyPrefs> = {};
    for (const [k, el] of Object.entries(inputs)) {
      if (!el) continue;
      const key = k as keyof PartyPrefs;
      if (el.type === 'checkbox') {
        (patch as Record<string, unknown>)[key] = el.checked;
      } else if (key === 'per_user_daily_usd_cap') {
        const trimmed = el.value.trim();
        (patch as Record<string, unknown>)[key] = trimmed === '' ? null : Number(trimmed);
      } else {
        (patch as Record<string, unknown>)[key] = Number(el.value);
      }
    }
    save.disabled = true;
    try {
      await setPartyPrefs(partyId, patch);
      ctx.setInfo(`Prefs updated for party ${partyId}.`);
      await renderParties(host, ctx);
    } catch (err) {
      ctx.setError((err as Error).message);
    } finally {
      save.disabled = false;
    }
  });
  buttonRow.appendChild(save);

  if (prefs.pacing_nudge) {
    const b = document.createElement('button');
    b.className = 'secondary';
    b.textContent = 'Clear pacing nudge';
    b.addEventListener('click', async () => {
      try { await setPartyPrefs(partyId, { pacing_nudge: null }); await renderParties(host, ctx); }
      catch (err) { ctx.setError((err as Error).message); }
    });
    buttonRow.appendChild(b);
  }
  if (prefs.director_hint) {
    const b = document.createElement('button');
    b.className = 'secondary';
    b.textContent = 'Clear director hint';
    b.addEventListener('click', async () => {
      try { await setPartyPrefs(partyId, { director_hint: null }); await renderParties(host, ctx); }
      catch (err) { ctx.setError((err as Error).message); }
    });
    buttonRow.appendChild(b);
  }

  const close = document.createElement('button');
  close.className = 'secondary';
  close.textContent = 'Close';
  close.addEventListener('click', () => { onClose(); });
  buttonRow.appendChild(close);

  editor.appendChild(buttonRow);
  return editor;
}
